import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card, EmptyState, SectionTitle, Badge, Money, formatDate, formatDateTime } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarClock, Plus, Pencil, Trash2, Loader2, X, Clock, ListChecks, Wheat, Printer, MapPin, Megaphone, Tag, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DetailDialog } from "./BakeDayDetail";

const emptyForm = () => ({
  title: "", pickup_date: "", preorder_close: "", notes: "", status: "draft",
  scheduled_announce_at: "",
  location_name: "", location_address: "", location_notes: "", pickup_instructions: "",
  visibility: "public", combine_by_recipe: true, round_whole_batches: false,
  pickup_windows: [{ start: "09:00", end: "10:00", max_orders: "" }],
  products: [],
});

export default function BakeDays() {
  const { bakery } = useAuth();
  const [days, setDays] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [optedOut, setOptedOut] = useState(0);

  const load = () => Promise.all([api.get("/bake-days"), api.get("/products"), api.get("/marketing/stats")])
    .then(([d, p, m]) => { setDays(d.data); setProducts(p.data.filter((x) => x.active)); setOptedOut(m.data.opted_out); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title, pickup_date: d.pickup_date, preorder_close: d.preorder_close ? d.preorder_close.slice(0, 16) : "",
      notes: d.notes || "", status: d.status,
      scheduled_announce_at: d.scheduled_announce_at ? d.scheduled_announce_at.slice(0, 16) : "",
      location_name: d.location_name || "", location_address: d.location_address || "",
      location_notes: d.location_notes || "", pickup_instructions: d.pickup_instructions || "",
      visibility: d.visibility || "public", combine_by_recipe: d.combine_by_recipe !== false,
      round_whole_batches: !!d.round_whole_batches,
      pickup_windows: d.pickup_windows.map((w) => ({ start: w.start, end: w.end, max_orders: w.max_orders ?? "" })),
      products: d.products.map((p) => ({ product_id: p.product_id, max_quantity: String(p.max_quantity) })),
    });
    setOpen(true);
  };

  const setWin = (i, k, v) => setForm((f) => ({ ...f, pickup_windows: f.pickup_windows.map((w, idx) => idx === i ? { ...w, [k]: v } : w) }));
  const addWin = () => setForm((f) => ({ ...f, pickup_windows: [...f.pickup_windows, { start: "", end: "", max_orders: "" }] }));
  const rmWin = (i) => setForm((f) => ({ ...f, pickup_windows: f.pickup_windows.filter((_, idx) => idx !== i) }));

  const toggleProduct = (id) => setForm((f) => {
    const exists = f.products.find((p) => p.product_id === id);
    return { ...f, products: exists ? f.products.filter((p) => p.product_id !== id) : [...f.products, { product_id: id, max_quantity: "10" }] };
  });
  const setMax = (id, v) => setForm((f) => ({ ...f, products: f.products.map((p) => p.product_id === id ? { ...p, max_quantity: v } : p) }));

  const save = async () => {
    if (!form.title.trim() || !form.pickup_date) return toast.error("Title and pickup date are required");
    if (form.products.length === 0) return toast.error("Add at least one product");
    setSaving(true);
    try {
      const payload = {
        title: form.title, pickup_date: form.pickup_date,
        preorder_close: form.preorder_close || null, notes: form.notes, status: form.status,
        scheduled_announce_at: form.scheduled_announce_at ? new Date(form.scheduled_announce_at).toISOString() : null,
        location_name: form.location_name, location_address: form.location_address,
        location_notes: form.location_notes, pickup_instructions: form.pickup_instructions,
        visibility: form.visibility, combine_by_recipe: form.combine_by_recipe, round_whole_batches: form.round_whole_batches,
        pickup_windows: form.pickup_windows.filter((w) => w.start && w.end).map((w) => ({ start: w.start, end: w.end, max_orders: w.max_orders === "" ? null : parseInt(w.max_orders) })),
        products: form.products.map((p) => ({ product_id: p.product_id, max_quantity: parseInt(p.max_quantity) || 0 })),
      };
      if (editing) await api.put(`/bake-days/${editing.id}`, payload);
      else await api.post("/bake-days", payload);
      toast.success(editing ? "Bake day updated" : "Bake day created");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStatus = async (d, status) => {
    await api.patch(`/bake-days/${d.id}/status`, { status });
    toast.success(`Marked ${status}`);
    if (status === "open" && d.status !== "open" && !d.announced_at) {
      if (window.confirm(`"${d.title}" is now open! Email your past customers to announce this drop?`)) {
        try {
          const { data } = await api.post(`/bake-days/${d.id}/announce`);
          toast.success(data.message);
        } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't send announcement"); }
      }
    }
    load();
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    await api.delete(`/bake-days/${d.id}`);
    toast.success("Deleted");
    load();
  };
  const announce = async (d) => {
    const msg = d.announced_at
      ? `You already announced "${d.title}" on ${formatDate(d.announced_at)}. Email past customers again?`
      : `Email all your past customers about "${d.title}"?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/bake-days/${d.id}/announce${d.announced_at ? "?force=true" : ""}`);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't send announcement"); }
  };

  const locationNames = [...new Set(days.map((d) => d.location_name).filter(Boolean))];
  const copyLink = (d) => {
    const url = `${window.location.origin}/b/${bakery?.slug}?day=${d.link_token}`;
    Promise.resolve(navigator.clipboard?.writeText(url))
      .then(() => toast.success("Private ordering link copied"))
      .catch(() => toast.error("Couldn't copy — copy it manually: " + url));
  };

  const productName = (id) => products.find((p) => p.id === id)?.name || days.flatMap((d) => d.products).length ? (products.find((p) => p.id === id)?.name || "Product") : "Product";

  return (
    <div>
      <SectionTitle action={
        <div className="flex items-center gap-3">
          {optedOut > 0 && <span className="text-xs text-cocoa/50" data-testid="optout-count">{optedOut} unsubscribed</span>}
          <Button onClick={openNew} className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="add-bakeday-btn"><Plus className="mr-1.5 h-4 w-4" /> New bake day</Button>
        </div>
      }>Bake Days</SectionTitle>

      {loading ? (
        <div className="space-y-4">{[...Array(2)].map((_, i) => <div key={i} className="h-32 animate-pulse rounded-2xl bg-cocoa/5" />)}</div>
      ) : days.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No bake days yet" body="Create a bake day with pickup windows and product quantity limits, then open it for orders and share your link." action={products.length ? <Button onClick={openNew} className="rounded-full bg-terracotta text-warmwhite" data-testid="add-first-bakeday">Create a bake day</Button> : <p className="text-sm text-cocoa/50">Add a product first, then create a bake day.</p>} />
      ) : (
        <div className="space-y-4">
          {days.map((d) => (
            <Card key={d.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2"><h3 className="font-serif text-xl text-cocoa">{d.title}</h3><Badge status={d.status} /><VisBadge v={d.visibility} /></div>
                  <p className="mt-1 text-sm text-cocoa/60">Pickup {formatDate(d.pickup_date)} · {d.pickup_windows.length} window{d.pickup_windows.length !== 1 ? "s" : ""} · {d.products.length} product{d.products.length !== 1 ? "s" : ""}</p>
                  {d.location_name && <p className="mt-0.5 flex items-center gap-1 text-xs text-cocoa/60"><MapPin className="h-3 w-3" /> {d.location_name}</p>}
                  {d.preorder_close && <p className="text-xs text-cocoa/50">Preorders close {formatDateTime(d.preorder_close)}</p>}
                  {d.announced_at && <p className="text-xs font-medium text-green-700">📣 Announced {formatDate(d.announced_at)} · {d.announced_count || 0} sent</p>}
                  {!d.announced_at && d.scheduled_announce_at && <p className="text-xs text-cocoa/50" data-testid={`scheduled-${d.id}`}>⏰ Announcement scheduled for {formatDateTime(d.scheduled_announce_at)}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {d.visibility === "link_only" && <Button variant="outline" size="sm" onClick={() => copyLink(d)} className="rounded-full border-cocoa/20 text-cocoa" data-testid={`copy-link-${d.id}`}><LinkIcon className="mr-1 h-3.5 w-3.5" /> Copy private link</Button>}
                  {d.status === "open" && <Button variant="outline" size="sm" onClick={() => announce(d)} className="rounded-full border-terracotta/30 text-terracotta hover:bg-terracotta/5" data-testid={`announce-${d.id}`}><Megaphone className="mr-1 h-3.5 w-3.5" /> Notify customers</Button>}
                  <Button variant="outline" size="sm" onClick={() => setDetail(d)} className="rounded-full border-cocoa/20 text-cocoa" data-testid={`view-bakeday-${d.id}`}>Production &amp; totals</Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(d)} aria-label={`Edit ${d.title}`} title="Edit bake day" className="rounded-full text-cocoa" data-testid={`edit-bakeday-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(d)} aria-label={`Delete ${d.title}`} title="Delete bake day" className="rounded-full text-destructive hover:bg-destructive/10" data-testid={`delete-bakeday-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {["draft", "open", "closed", "completed"].map((s) => (
                  <button key={s} onClick={() => setStatus(d, s)} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${d.status === s ? "bg-terracotta text-warmwhite" : "bg-cocoa/5 text-cocoa/60 hover:bg-cocoa/10"}`} data-testid={`status-${s}-${d.id}`}>
                    {s === "open" ? "Open for orders" : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {d.products.map((p) => {
                  const sold = p.max_quantity - p.remaining;
                  return (
                    <div key={p.product_id} className="flex items-center justify-between rounded-xl bg-cream px-3 py-2 text-sm">
                      <span className="text-cocoa">{products.find((x) => x.id === p.product_id)?.name || "Product"}</span>
                      <span className={p.remaining <= 0 ? "font-medium text-terracotta" : "text-cocoa/50"}>{p.remaining <= 0 ? "Sold out" : `${sold}/${p.max_quantity}`}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto bg-warmwhite">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-cocoa">{editing ? "Edit bake day" : "New bake day"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div><Label className="text-cocoa">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Saturday Sourdough Drop" className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-cocoa">Pickup date *</Label><Input type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-date" /></div>
              <div><Label className="text-cocoa">Preorders close</Label><Input type="datetime-local" value={form.preorder_close} onChange={(e) => setForm({ ...form, preorder_close: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-close" /></div>
            </div>

            <div>
              <Label className="text-cocoa">Pickup windows</Label>
              <div className="mt-1.5 space-y-2">
                {form.pickup_windows.map((w, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="time" value={w.start} onChange={(e) => setWin(i, "start", e.target.value)} className="rounded-xl bg-cream" data-testid={`win-start-${i}`} />
                    <span className="text-cocoa/40">–</span>
                    <Input type="time" value={w.end} onChange={(e) => setWin(i, "end", e.target.value)} className="rounded-xl bg-cream" data-testid={`win-end-${i}`} />
                    <Input type="number" value={w.max_orders} onChange={(e) => setWin(i, "max_orders", e.target.value)} placeholder="Max" className="w-24 rounded-xl bg-cream" data-testid={`win-max-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => rmWin(i)} aria-label="Remove pickup window" title="Remove window" className="rounded-full text-cocoa/40"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addWin} className="mt-2 rounded-full border-cocoa/20 text-cocoa" data-testid="add-window-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Add window</Button>
              <p className="mt-1 text-xs text-cocoa/40">Leave "Max" empty for unlimited orders per window.</p>
            </div>

            <div>
              <Label className="text-cocoa">Products &amp; quantity limits *</Label>
              <div className="mt-1.5 space-y-2">
                {products.length === 0 && <p className="text-sm text-cocoa/50">Add products first from the Products page.</p>}
                {products.map((p) => {
                  const sel = form.products.find((x) => x.product_id === p.id);
                  return (
                    <div key={p.id} className="flex items-center gap-3 rounded-xl bg-cream px-3 py-2">
                      <Checkbox checked={!!sel} onCheckedChange={() => toggleProduct(p.id)} data-testid={`bd-product-${p.id}`} />
                      <span className="flex-1 text-sm text-cocoa">{p.name}</span>
                      {sel && <div className="flex items-center gap-2"><span className="text-xs text-cocoa/50">Max</span><Input type="number" value={sel.max_quantity} onChange={(e) => setMax(p.id, e.target.value)} className="w-20 rounded-xl bg-warmwhite" data-testid={`bd-max-${p.id}`} /></div>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-cocoa">Pickup location (optional)</Label>
              <datalist id="location-names">{locationNames.map((n) => <option key={n} value={n} />)}</datalist>
              <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                <Input list="location-names" value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} placeholder="e.g. Mueller Farmers Market" className="rounded-xl bg-cream" data-testid="bakeday-location-name" />
                <Input value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} placeholder="Address (optional)" className="rounded-xl bg-cream" data-testid="bakeday-location-address" />
              </div>
              <p className="mt-1 text-xs text-cocoa/40">Run different bake days at different markets or pickup spots.</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="text-cocoa">Visibility</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public — shown on storefront</SelectItem>
                    <SelectItem value="link_only">Link only — private ordering link</SelectItem>
                    <SelectItem value="private">Private — dashboard only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-cocoa">Pickup instructions (this bake day)</Label>
                <Input value={form.pickup_instructions} onChange={(e) => setForm({ ...form, pickup_instructions: e.target.value })} placeholder="e.g. Text when you arrive" className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-pickup-instructions" />
              </div>
            </div>
            <div><Label className="text-cocoa">Location notes (optional)</Label>
              <Input value={form.location_notes} onChange={(e) => setForm({ ...form, location_notes: e.target.value })} placeholder="e.g. Park in the rear lot" className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-location-notes" />
            </div>
            <div className="space-y-2 rounded-xl bg-cream p-3">
              <label className="flex items-center gap-2 text-sm text-cocoa"><Checkbox checked={form.combine_by_recipe} onCheckedChange={(v) => setForm({ ...form, combine_by_recipe: !!v })} data-testid="bakeday-combine" /> Combine products that use the same recipe</label>
              <label className="flex items-center gap-2 text-sm text-cocoa"><Checkbox checked={form.round_whole_batches} onCheckedChange={(v) => setForm({ ...form, round_whole_batches: !!v })} data-testid="bakeday-round" /> Round up to whole batches</label>
            </div>

            <div>
              <Label className="text-cocoa">Schedule announcement (optional)</Label>
              <Input type="datetime-local" value={form.scheduled_announce_at} onChange={(e) => setForm({ ...form, scheduled_announce_at: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-schedule-announce" />
              <p className="mt-1 text-xs text-cocoa/40">We'll email your past customers automatically at this time (once the day is Open).</p>
            </div>

            <div>
              <Label className="text-cocoa">Notes for customers</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-xl bg-cream" rows={2} data-testid="bakeday-notes" />
            </div>
            <div>
              <Label className="text-cocoa">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="bakeday-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="open">Open for ordering</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={save} disabled={saving} className="w-full rounded-full bg-terracotta py-6 text-warmwhite hover:bg-terracotta/90" data-testid="save-bakeday-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save bake day"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {detail && <DetailDialog day={detail} products={products} onClose={() => setDetail(null)} />}
    </div>
  );
}

function VisBadge({ v }) {
  const m = { public: ["Public", "bg-sage/20 text-sage"], link_only: ["Link only", "bg-amber-500/20 text-amber-700"], private: ["Private", "bg-cocoa/10 text-cocoa/60"] };
  const [lbl, cls] = m[v || "public"] || m.public;
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`} data-testid="visibility-badge">{lbl}</span>;
}
